import React, { useState } from 'react';
import { Video, Link2, AlertCircle, X, CheckCircle2, Plus, Trash2, Play, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';

const SortableFeature = ({ feature, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: `feature-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 bg-white p-3 rounded-xl"
    >
      <div {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="h-5 w-5 text-gray-400" />
      </div>
      <span className="flex-1">{feature}</span>
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="text-red-600 hover:text-red-700"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

const ProductDemo = ({ formData, onInputChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [newFeature, setNewFeature] = useState('');
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.includes('video')) {
      setError('Please upload a video file');
      return;
    }

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setError('Video size must be less than 100MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `product-demo-${timestamp}-${file.name}`;
      
      // Create Firebase Storage reference
      const storageRef = ref(storage, `product-demos/${fileName}`);
      
      // Upload file to Firebase Storage
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update form data with permanent URL
      onInputChange('productDemo', 'videoUrl', downloadURL);
      onInputChange('productDemo', 'videoName', file.name);
      onInputChange('productDemo', 'lastUpdated', new Date().toISOString());
      
    } catch (error) {
      console.error('Error uploading video:', error);
      setError('Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveVideo = async () => {
    try {
      // If there's a Firebase Storage URL, we could delete the file
      // For now, we'll just clear the form data
      // TODO: Implement file deletion from Firebase Storage if needed
      
      onInputChange('productDemo', 'videoUrl', '');
      onInputChange('productDemo', 'videoName', '');
      onInputChange('productDemo', 'lastUpdated', null);
      setShowVideoPreview(false);
    } catch (error) {
      console.error('Error removing video:', error);
      setError('Failed to remove video. Please try again.');
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    
    const currentFeatures = formData.productDemo.features || [];
    onInputChange('productDemo', 'features', [...currentFeatures, newFeature.trim()]);
    setNewFeature('');
  };

  const handleRemoveFeature = (index) => {
    const currentFeatures = formData.productDemo.features || [];
    const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
    onInputChange('productDemo', 'features', updatedFeatures);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const currentFeatures = formData.productDemo.features || [];
      const oldIndex = currentFeatures.findIndex((_, i) => `feature-${i}` === active.id);
      const newIndex = currentFeatures.findIndex((_, i) => `feature-${i}` === over.id);
      
      onInputChange('productDemo', 'features', arrayMove(currentFeatures, oldIndex, newIndex));
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return '';
    
    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i)?.[1];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    
    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      const videoId = url.match(/(?:vimeo\.com\/)(\d+)/i)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }
    
    // Handle Loom URLs
    if (url.includes('loom.com')) {
      const loomId = url.split('/').pop();
      return `https://www.loom.com/embed/${loomId}`;
    }
    
    // For Firebase Storage URLs or direct video files, return the URL as-is
    // These will be handled by the HTML5 video player
    return url;
  };

  const isExternalVideo = (url) => {
    return url && (
      url.includes('youtube.com') || 
      url.includes('youtu.be') || 
      url.includes('vimeo.com') || 
      url.includes('loom.com')
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        {/* Demo Video Upload */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white rounded-xl shadow-sm">
              <Video className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-900">Product Demo Video</h3>
          </div>

          {!formData.productDemo.videoUrl ? (
            <div className="space-y-4">
              <div>
                <label className={`inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white cursor-pointer transition-all ${
                  uploading 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                }`}>
                  <input
                    type="file"
                    className="hidden"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    disabled={uploading}
                  />
                  {uploading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </div>
                  ) : (
                    'Upload Video'
                  )}
                </label>
              </div>
              <div className="relative">
                <input
                  type="url"
                  value={formData.productDemo.videoUrl || ''}
                  onChange={(e) => onInputChange('productDemo', 'videoUrl', e.target.value)}
                  placeholder="Or paste video URL here"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span>
                  {formData.productDemo.videoName || 'Video URL'} • Last updated{' '}
                  {formData.productDemo.lastUpdated ? new Date(formData.productDemo.lastUpdated).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowVideoPreview(!showVideoPreview)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                >
                  <Play className="h-4 w-4" />
                  {showVideoPreview ? 'Hide Video' : 'Watch Video'}
                </button>
                <button
                  type="button"
                  onClick={handleRemoveVideo}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove Video
                </button>
              </div>
              {showVideoPreview && (
                <div className="aspect-video w-full bg-black rounded-xl overflow-hidden">
                  {isExternalVideo(formData.productDemo.videoUrl) ? (
                    <iframe
                      src={getVideoEmbedUrl(formData.productDemo.videoUrl)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Product Demo Video"
                    />
                  ) : (
                    <video
                      src={formData.productDemo.videoUrl}
                      className="w-full h-full"
                      controls
                      playsInline
                      preload="metadata"
                      onError={(e) => {
                        console.error('Video loading error:', e);
                        setError('Failed to load video. Please check the URL or try uploading again.');
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Features List */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white rounded-xl shadow-sm">
              <CheckCircle2 className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-900">Key Features</h3>
          </div>

          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Add a new feature"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
              />
              <button
                type="button"
                onClick={handleAddFeature}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={(formData.productDemo.features || []).map((_, index) => `feature-${index}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {(formData.productDemo.features || []).map((feature, index) => (
                    <SortableFeature
                      key={`feature-${index}`}
                      feature={feature}
                      index={index}
                      onRemove={handleRemoveFeature}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Demo Link */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white rounded-xl shadow-sm">
              <Link2 className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-900">Live Demo Link</h3>
          </div>

          <input
            type="url"
            value={formData.productDemo.demoLink || ''}
            onChange={(e) => onInputChange('productDemo', 'demoLink', e.target.value)}
            placeholder="https://your-demo-url.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 rounded-xl flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 rounded-xl">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Product Demo</h3>
        <ul className="text-sm text-blue-700 space-y-2">
          <li>• Keep your demo video concise (2-3 minutes)</li>
          <li>• Focus on key features and value proposition</li>
          <li>• Ensure high-quality video and audio</li>
          <li>• Include a clear call-to-action</li>
          <li>• Provide a live demo link for interactive experience</li>
        </ul>
      </div>
    </div>
  );
};

export default ProductDemo; 